import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { User } from '../types'

interface AuthContextType {
  user: User | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  logout: () => void
  verifyToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('auth_token')
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async (): Promise<boolean> => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      setLoading(false)
      return false
    }

    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        setLoading(false)
        return true
      } else if (response.status === 401) {
        // Token is invalid or expired - remove it
        localStorage.removeItem('auth_token')
        setUser(null)
        setLoading(false)
        return false
      } else {
        // Other errors (500, 502, 503, 504, etc.) - keep token, just mark as not verified
        console.error(`Token verification failed with status ${response.status}`)
        setLoading(false)
        return false
      }
    } catch (error) {
      // Network error, timeout, etc. - keep the token, don't log out
      console.error('Error verifying token:', error)
      setLoading(false)
      return false
    }
  }

  const signInWithGoogle = async () => {
    try {
      // Capture referral code from URL before redirect (if present)
      const urlParams = new URLSearchParams(window.location.search)
      const referralCode = urlParams.get('ref')
      if (referralCode) {
        sessionStorage.setItem('referral_code', referralCode)
      }
      
      // Get Google OAuth URL
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
      const redirectUri = `${window.location.origin}/auth/callback`
      const scope = 'openid email profile'
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${googleClientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=code&` +
        `access_type=offline`

      // Redirect to Google OAuth
      window.location.href = authUrl
    } catch (error) {
      console.error('Error initiating Google sign-in:', error)
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    setUser(null)
  }

  const value = {
    user,
    loading,
    signInWithGoogle,
    logout,
    verifyToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}